import React, { useRef, useState } from 'react';
import Icon from '../Icon';
import { translate } from '../../Services/translation';
import { useIsBendixTheme } from '../../Hooks/ChannelHooks';
import BoxDialog from '../BoxDialog';

const QuantityInput = ({
  quantity,
  setQuantity,
  packageSize,
  allowBrokenBox = false,
  stockQuantity = 0,
  onCommit,
  onConfirmBrokenBox,
}) => {
  const isBendix = useIsBendixTheme();
  const [boxDialogOpen, setBoxDialogOpen] = useState(false);
  const [dialogQuantity, setDialogQuantity] = useState(null);
  // Snapshot of the input value when focus enters. Used by handleQuantity to skip
  // the broken-box dialog when the user blurs the input without actually editing
  // it (clicked in, clicked out) — otherwise the dialog can fire seemingly at
  // random on rows that already contain a broken-box value.
  const focusValueRef = useRef(null);

  const commit = (value) => {
    focusValueRef.current = null;
    setQuantity(value);
    if (onCommit) onCommit(value);
  };

  const handleDecrease = () => {
    const qty = parseInt(quantity);
    if (!qty || qty <= packageSize) return;
    const prev = Math.round((Math.ceil(qty / packageSize) - 1) * packageSize);
    commit(prev);
  };

  const handleIncrease = () => {
    const qty = parseInt(quantity) || 0;
    const next = Math.round((Math.floor(qty / packageSize) + 1) * packageSize);
    commit(next);
  };

  const handleQuantity = () => {
    // While the dialog is open, swallow the blur that fires when the user clicks
    // the dialog's confirm button (which moves focus away from the input). Without
    // this, the dialog would re-open on top of itself for the same value.
    if (boxDialogOpen) return;

    // No-op when the value hasn't changed since focus — prevents the broken-box
    // dialog from re-firing every time the user merely tabs through or clicks into
    // the input without editing.
    if (
      focusValueRef.current !== null &&
      String(quantity ?? '') === focusValueRef.current
    ) {
      focusValueRef.current = null;
      return;
    }

    if (!quantity && quantity !== 0) {
      commit(packageSize);
      return;
    }
    const qty = parseInt(quantity);
    const roundUp = Math.round(
      Math.ceil(Math.max(packageSize, qty) / packageSize) * packageSize
    );

    if (isBendix && qty !== roundUp && allowBrokenBox) {
      if (stockQuantity > 0 && qty >= stockQuantity) {
        commit(qty);
        return;
      }
      setDialogQuantity(qty);
      setBoxDialogOpen(true);
      return;
    }

    commit(roundUp);
  };

  const handleSelect = (value) => {
    commit(value);
    setBoxDialogOpen(false);
    if (onConfirmBrokenBox) onConfirmBrokenBox(value);
  };

  return (
    <div className="quantity-input__wrapper">
      {isBendix && (
        <BoxDialog
          isOpen={boxDialogOpen}
          onClose={() => setBoxDialogOpen(false)}
          quantity={dialogQuantity ?? packageSize}
          packageSize={packageSize}
          onSelect={handleSelect}
        />
      )}
      <button
        className="quantity-input__button"
        onClick={handleDecrease}
        aria-label={translate('buybutton.quantity.decrease.sc')}
        disabled={parseInt(quantity) <= packageSize}
      >
        <Icon
          name="Minus"
          size={isBendix ? 20 : 24}
          className="quantity-input__icon"
        />
      </button>
      <input
        className="quantity-input"
        type="number"
        value={quantity}
        min={packageSize}
        onChange={(e) => setQuantity(e.target.value)}
        onFocus={(e) => {
          focusValueRef.current = String(e.target.value);
        }}
        onBlur={() => handleQuantity()}
        onKeyDown={(e) => e.key === 'Enter' && handleQuantity()}
      />
      <button
        className="quantity-input__button"
        onClick={handleIncrease}
        aria-label={translate('buybutton.quantity.increase.sc')}
      >
        <Icon
          name="Plus"
          size={isBendix ? 20 : 24}
          className="quantity-input__icon"
        />
      </button>
    </div>
  );
};

export default QuantityInput;
